import { ApiList, ApiResponse } from "types/api.types";
import { commaSeparatedEnums, positiveInt } from "utils/list-query";
import {
  CreateWorkAssignmentRequest,
  ReassignWorkAssignmentRequest,
  WorkAssignment,
} from "features/WorkAssignments/types/work-assignment.types";
import { normalizeWorkAssignment } from "features/WorkAssignments/utils/workAssignmentNormalize";
import {
  WorkAssignmentSourceType,
  WORK_ASSIGNMENT_LIST_END_SOURCES,
  WORK_ASSIGNMENT_LIST_STATUSES,
} from "features/WorkAssignments/constants/work-assignment.constants";
import axiosClient from "utils/axios";

const LIST_SOURCE_TYPES = Object.values(WorkAssignmentSourceType);

function trimQueryDate(v: string | undefined): string | undefined {
  const t = v?.trim();
  return t ? t : undefined;
}

export type GetWorkAssignmentsListParams = {
  page?: number;
  limit?: number;
  offset?: number;
  search?: string;
  /** Comma-separated or array: ASSIGNED, IN_PROGRESS, COMPLETED */
  status?: string | string[];
  /** Filter by assignee (`to_user_id` on server; legacy `userId` query still accepted by some APIs). */
  toUserId?: number | string;
  /** @deprecated Prefer {@link toUserId}; sent as `userId` for older backends. */
  userId?: number | string;
  /** Comma-separated or array of source types (validated server-side). */
  sourceType?: string | string[] | WorkAssignmentSourceType;
  sourceId?: number | string;
  endSource?: string | string[];
  createdBy?: number | string;
  plannedStartFrom?: string;
  plannedStartTo?: string;
  plannedEndFrom?: string;
  plannedEndTo?: string;
  actualStartFrom?: string;
  actualStartTo?: string;
  actualEndFrom?: string;
  actualEndTo?: string;
  createdFrom?: string;
  createdTo?: string;
  /** Server-side schedule hint filter: NOT_STARTED | OVERDUE | STARTED_LATE | ON_TRACK | COMPLETED_LATE */
  scheduleStatus?: string;
};

export const getWorkAssignments = async (
  params: GetWorkAssignmentsListParams = {},
) => {
  try {
    const {
      page,
      limit,
      offset,
      search,
      status,
      toUserId,
      userId,
      sourceType,
      sourceId,
      endSource,
      createdBy,
      plannedStartFrom,
      plannedStartTo,
      plannedEndFrom,
      plannedEndTo,
      actualStartFrom,
      actualStartTo,
      actualEndFrom,
      actualEndTo,
      createdFrom,
      createdTo,
      scheduleStatus,
    } = params;

    const query: Record<string, string | number> = {};

    const p = positiveInt(page);
    if (p != null) query.page = p;
    const l = positiveInt(limit);
    if (l != null) query.limit = l;
    const o = positiveInt(offset);
    if (o != null) query.offset = o;

    const s = search != null ? String(search).trim() : "";
    if (s) query.search = s;

    const statusCsv = commaSeparatedEnums(status, [
      ...WORK_ASSIGNMENT_LIST_STATUSES,
    ]);
    if (statusCsv) query.status = statusCsv;

    const sourceTypeCsv = commaSeparatedEnums(
      sourceType as string | string[] | undefined,
      LIST_SOURCE_TYPES,
    );
    if (sourceTypeCsv) query.sourceType = sourceTypeCsv;

    const endCsv = commaSeparatedEnums(
      endSource,
      [...WORK_ASSIGNMENT_LIST_END_SOURCES],
    );
    if (endCsv) query.endSource = endCsv;

    const assigneeId = positiveInt(toUserId ?? userId);
    if (assigneeId != null) {
      query.toUserId = assigneeId;
      query.userId = assigneeId;
    }

    const sid = positiveInt(sourceId);
    if (sid != null) query.sourceId = sid;

    const cb = positiveInt(createdBy);
    if (cb != null) query.createdBy = cb;

    const d = {
      plannedStartFrom: trimQueryDate(plannedStartFrom),
      plannedStartTo: trimQueryDate(plannedStartTo),
      plannedEndFrom: trimQueryDate(plannedEndFrom),
      plannedEndTo: trimQueryDate(plannedEndTo),
      actualStartFrom: trimQueryDate(actualStartFrom),
      actualStartTo: trimQueryDate(actualStartTo),
      actualEndFrom: trimQueryDate(actualEndFrom),
      actualEndTo: trimQueryDate(actualEndTo),
      createdFrom: trimQueryDate(createdFrom),
      createdTo: trimQueryDate(createdTo),
    };
    (Object.keys(d) as (keyof typeof d)[]).forEach((k) => {
      const v = d[k];
      if (v) query[k] = v;
    });

    if (scheduleStatus) query.scheduleStatus = scheduleStatus;

    const response = await axiosClient.get<
      ApiResponse<ApiList<WorkAssignment[]>>
    >("/work-assignments", { params: query });
    const body = response.data;
    if (body.success && body.data?.rows?.length) {
      return {
        ...body,
        data: {
          ...body.data,
          rows: body.data.rows.map(normalizeWorkAssignment),
        },
      };
    }
    return body;
  } catch (err) {
    console.warn("Error fetching work assignments: ", err);
    throw err;
  }
};

export const getWorkAssignment = async (id: number) => {
  try {
    const response = await axiosClient.get<ApiResponse<WorkAssignment>>(
      `/work-assignments/${id}`,
    );
    const body = response.data;
    if (body.success && body.data) {
      return { ...body, data: normalizeWorkAssignment(body.data) };
    }
    return body;
  } catch (err) {
    console.warn("Error fetching work assignment: ", err);
    throw err;
  }
};

export const createWorkAssignment = async (
  payload: CreateWorkAssignmentRequest,
) => {
  try {
    const response = await axiosClient.post<
      ApiResponse<ApiList<WorkAssignment[]>>
    >("/work-assignments", payload);
    const body = response.data;
    if (body.success && body.data?.rows?.length) {
      return {
        ...body,
        data: {
          ...body.data,
          rows: body.data.rows.map(normalizeWorkAssignment),
        },
      };
    }
    return body;
  } catch (err) {
    console.warn("Error creating work assignment: ", err);
    throw err;
  }
};

export type UpdateWorkAssignmentPayload = {
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  role?: string | null;
  remarks?: string | null;
};

export const updateWorkAssignment = async (
  id: number,
  payload: UpdateWorkAssignmentPayload,
) => {
  try {
    const response = await axiosClient.patch<ApiResponse<WorkAssignment>>(
      `/work-assignments/${id}`,
      payload,
    );
    const body = response.data;
    if (body.success && body.data) {
      return { ...body, data: normalizeWorkAssignment(body.data) };
    }
    return body;
  } catch (err) {
    console.warn("Error updating work assignment: ", err);
    throw err;
  }
};

export const reassignWorkAssignment = async (
  payload: ReassignWorkAssignmentRequest,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkAssignment>>(
      `/work-assignments/reassign`,
      payload,
    );
    const body = response.data;
    if (body.success && body.data) {
      return { ...body, data: normalizeWorkAssignment(body.data) };
    }
    return body;
  } catch (err) {
    console.warn("Error reassigning work assignment: ", err);
    throw err;
  }
};

export type StartWorkAssignmentBody = {
  /** Optional backfill only; default is server “now”. */
  actualStartDate?: string;
};

export const startWorkAssignment = async (
  id: number,
  body: StartWorkAssignmentBody = {},
) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkAssignment>>(
      `/work-assignments/${id}/start`,
      Object.keys(body).length ? body : {},
    );
    const resBody = response.data;
    if (resBody.success && resBody.data) {
      return { ...resBody, data: normalizeWorkAssignment(resBody.data) };
    }
    return resBody;
  } catch (err) {
    console.warn("Error starting work assignment: ", err);
    throw err;
  }
};

export type CompleteWorkAssignmentBody = {
  actualEndDate?: string;
  endSource?: "MANUAL" | "AUTO";
};

/** Finishes work (`IN_PROGRESS` → `COMPLETED`). Same behaviour as legacy `end-assignment`. */
export const completeWorkAssignment = async (
  id: number,
  body: CompleteWorkAssignmentBody = {},
) => {
  try {
    const response = await axiosClient.post<ApiResponse<WorkAssignment>>(
      `/work-assignments/${id}/complete`,
      Object.keys(body).length ? body : {},
    );
    const resBody = response.data;
    if (resBody.success && resBody.data) {
      return { ...resBody, data: normalizeWorkAssignment(resBody.data) };
    }
    return resBody;
  } catch (err) {
    console.warn("Error completing work assignment: ", err);
    throw err;
  }
};

/** @deprecated Prefer {@link completeWorkAssignment}; kept for call sites. */
export const endWorkAssignment = async (id: number) => {
  return completeWorkAssignment(id, {});
};

export const deleteWorkAssignment = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<null>>(
      `/work-assignments/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting work assignment: ", err);
    throw err;
  }
};

export type WorkAssignmentScheduleCounts = {
  all: number;
  NOT_STARTED: number;
  OVERDUE: number;
  STARTED_LATE: number;
  ON_TRACK: number;
  COMPLETED_LATE: number;
};

export const getWorkAssignmentScheduleCounts = async (params: {
  toUserId?: number;
  sourceType?: string;
  sourceId?: number;
} = {}): Promise<WorkAssignmentScheduleCounts> => {
  const query: Record<string, string | number> = {};
  const assigneeId = positiveInt(params.toUserId);
  if (assigneeId != null) query.toUserId = assigneeId;
  const srcType = params.sourceType?.trim();
  if (srcType) query.sourceType = srcType;
  const srcId = positiveInt(params.sourceId);
  if (srcId != null) query.sourceId = srcId;

  const response = await axiosClient.get<ApiResponse<WorkAssignmentScheduleCounts>>(
    "/work-assignments/schedule-counts",
    { params: query },
  );
  return response.data.data as WorkAssignmentScheduleCounts;
};
